"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { stripLocalePrefix } from "@/i18n/routing";

/**
 * PostHog: consent-gated ANALYTICS + always-on ERROR capture.
 *
 *  1. **Analytics is consent-gated.** Product-analytics events (pageviews,
 *     `capture()` calls) are withheld until the visitor accepts via the banner,
 *     and NO PostHog cookie is set before then — persistence starts as `memory`
 *     and is upgraded to `localStorage+cookie` only on consent. Satisfies
 *     GDPR/ePrivacy (EU/UK) + PDPD (Vietnam) for the analytics cookie.
 *  2. **Errors are captured for everyone.** Exceptions (`$exception`, from
 *     `capture_exceptions` autocapture + the error-boundary `captureException`
 *     calls) are sent regardless of consent and on EVERY route (including
 *     internal/authenticated surfaces) — crash reporting to keep the service
 *     working, on a legitimate-interest basis, without a cookie pre-consent.
 *     Disclosed on `/cookies`. `before_send` is the gate: always let
 *     `$exception` through; drop analytics until consent (and never on internal
 *     surfaces). Server-side exceptions are captured separately in
 *     `src/instrumentation.ts`.
 *  3. **No-op without a key** — in local/preview (no `NEXT_PUBLIC_POSTHOG_KEY`)
 *     nothing initializes and the consent banner stays hidden. And nothing is
 *     SENT from localhost even when a key is present (see `before_send`).
 *
 * Deliberately does NOT use `usePathname`/`useSearchParams` — those would make
 * the static Nav shell dynamic on `[slug]` routes (see CLAUDE.md PPR notes).
 * PostHog's built-in `capture_pageview` handles navigation instead.
 *
 * Session replay is OFF for v1 (`disable_session_recording`).
 */

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";
const CONSENT_COOKIE = "et_analytics_consent";
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180; // 6 months

// Module-level so `before_send` (a closure fixed at init time) always reads the
// CURRENT analytics-consent decision. Errors ignore this flag; analytics obey it.
let analyticsConsentGranted = false;

type ConsentStatus = "granted" | "denied" | null;

function readConsentCookie(): ConsentStatus {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)et_analytics_consent=(granted|denied)/);
  return (m?.[1] as ConsentStatus) ?? null;
}

function writeConsentCookie(value: "granted" | "denied") {
  document.cookie = `${CONSENT_COOKIE}=${value}; path=/; max-age=${CONSENT_MAX_AGE}; samesite=lax`;
}

type ConsentContextValue = {
  /** Whether analytics is even configured (key present). Banner/links hide when false. */
  analyticsEnabled: boolean;
  consent: ConsentStatus;
  /** Show the banner: analytics enabled, and either no decision yet or the user reopened it. */
  showBanner: boolean;
  grant: () => void;
  deny: () => void;
  /** Re-open the banner (footer "Manage cookies"). */
  reopen: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function useConsent(): ConsentContextValue {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within <PostHogProvider>");
  return ctx;
}

let posthogInitialized = false;

export default function PostHogProvider({ children }: { children: ReactNode }) {
  const analyticsEnabled = Boolean(POSTHOG_KEY);
  const [consent, setConsent] = useState<ConsentStatus>(null);
  const [reopened, setReopened] = useState(false);
  // Gate the banner on hydration: SSR/first paint renders nothing, and the banner
  // only appears after the cookie is read — so returning (decided) users on a hard
  // load never see a flash of a banner that immediately hides.
  const [hydrated, setHydrated] = useState(false);

  // Initialize once. Capturing is ON so exceptions send immediately; analytics
  // are withheld by `before_send` until consent, and no cookie is set pre-consent
  // (memory persistence). Reads the stored decision to seed both.
  useEffect(() => {
    if (!POSTHOG_KEY) {
      setHydrated(true);
      return;
    }
    const stored = readConsentCookie();
    analyticsConsentGranted = stored === "granted";

    if (!posthogInitialized) {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: "identified_only",
        capture_pageview: true,
        disable_session_recording: true, // replay off for v1
        capture_exceptions: true, // error tracking: autocapture unhandled exceptions
        // ⛔ NO EXTERNALLY-LOADED BUNDLES — THEY BREAK REACT HYDRATION.
        // posthog-js lazily injects <script> tags into <body> for its optional
        // features (surveys, exception autocapture, dead clicks, heatmaps), and
        // it does so WHILE React is hydrating. Captured from the dev overlay's
        // own diff on /about:
        //   <script
        //   +   type="application/ld+json"   <- expected: the layout's JSON-LD
        //   -   type="text/javascript"
        //   -   src="https://eu-assets.i.posthog.com/array/phc_.../array.js"
        // React reached that slot, found a foreign <script>, and regenerated
        // the tree. Rate before: production 8/8 loads, local prod build 3/8,
        // dev 5/8 — it tracks latency, which is why it looked intermittent.
        //
        // ⚠️ WHAT THIS COSTS — narrower than it first appears. `captureException`
        // is a CORE method, so the three paths that already report errors keep
        // working untouched: `global-error.tsx`, `[locale]/error.tsx` and the
        // SERVER-side `instrumentation.ts` (`onRequestError`). The external
        // bundle only supplies the automatic observer for errors that reach
        // `window.onerror` / `unhandledrejection` OUTSIDE a React boundary —
        // and that gap is closed by hand below, so nothing is actually lost.
        // Core analytics is unaffected: pageviews and explicit capture() calls
        // ride the main npm bundle.
        //
        // Two other fixes were tried and reverted (see tickets.md): deferring
        // init to `window.load` (5/8 — `load` fires before hydration finishes
        // in a streaming app) and moving the JSON-LD script to the front of
        // <body> (7/8 — posthog-js inserts before the FIRST <script>, so ours
        // just became the new anchor; the fix is not positional).
        disable_external_dependency_loading: true,
        // No PostHog cookie until consent — upgraded to localStorage+cookie in grant().
        persistence: analyticsConsentGranted ? "localStorage+cookie" : "memory",
        before_send: (event) => {
          if (!event) return event;
          // ⛔ NOTHING FROM LOCAL DEVELOPMENT (2026-09-20). `.env.local` carries
          // the real key, so a dev session reported into the SAME project as
          // production: "getGuides is not defined", "LANGUAGE_PREF_ANY is not
          // defined" and a deliberately broken font family all sat in the live
          // error list next to real incidents, and two of them came from an
          // agent's own testing. Checked here rather than at init so the whole
          // pipeline still runs locally and only the send is dropped. Deploy
          // previews are NOT localhost and keep reporting.
          if (typeof window !== "undefined" && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname)) return null;
          // Errors always send — every route, consented or not (crash reporting).
          if (event.event === "$exception") return event;
          // Analytics events: only after consent...
          if (!analyticsConsentGranted) return null;
          // ...and never on internal/authenticated areas (admin, dashboards,
          // Studio). Reads window.location (not a React hook), so it can't make
          // the static shell dynamic.
          if (typeof window !== "undefined") {
            const path = stripLocalePrefix(window.location.pathname);
            if (/^\/(admin|dashboard|pro|studio)(\/|$)/.test(path)) return null;
          }
          return event;
        },
      });
      posthogInitialized = true;
    }

    // ⭐ REPLACES POSTHOG'S EXCEPTION AUTOCAPTURE, which `disable_external_
    // dependency_loading` above switches off along with the rest of the
    // externally-loaded bundles. React's error boundaries already report render
    // errors, and `instrumentation.ts` reports server ones; what those miss is
    // an error that never reaches a boundary — a throw inside a `setTimeout`, an
    // event handler, or a rejected promise nobody caught. These two listeners
    // cover exactly that, calling the same CORE `captureException` the
    // boundaries use, so the signal is the same and NOTHING is injected into the
    // DOM. Errors are deliberately reported regardless of consent, matching
    // `before_send` above, which always lets `$exception` through: crash
    // reporting is not analytics.
    const onError = (e: ErrorEvent) => {
      if (!posthogInitialized) return;
      posthog.captureException(e.error ?? new Error(e.message), { $exception_source: "window.onerror" });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (!posthogInitialized) return;
      const r: unknown = e.reason;
      posthog.captureException(r instanceof Error ? r : new Error(String(r)), {
        $exception_source: "unhandledrejection",
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    setConsent(stored);
    setHydrated(true);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  const grant = useCallback(() => {
    writeConsentCookie("granted");
    setConsent("granted");
    setReopened(false);
    analyticsConsentGranted = true;
    if (POSTHOG_KEY && posthogInitialized) {
      // Upgrade memory → persistent identity, then log the current view (the
      // initial pre-consent pageview was withheld by before_send).
      posthog.set_config({ persistence: "localStorage+cookie" });
      posthog.capture("$pageview");
    }
  }, []);

  const deny = useCallback(() => {
    writeConsentCookie("denied");
    setConsent("denied");
    setReopened(false);
    analyticsConsentGranted = false;
    if (POSTHOG_KEY && posthogInitialized) {
      // Drop any stored identity/cookie; exceptions still capture (memory).
      posthog.set_config({ persistence: "memory" });
      posthog.reset();
    }
  }, []);

  const reopen = useCallback(() => setReopened(true), []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      analyticsEnabled,
      consent,
      showBanner: analyticsEnabled && hydrated && (consent === null || reopened),
      grant,
      deny,
      reopen,
    }),
    [analyticsEnabled, hydrated, consent, reopened, grant, deny, reopen],
  );

  const tree = POSTHOG_KEY ? <PHProvider client={posthog}>{children}</PHProvider> : children;

  return <ConsentContext.Provider value={value}>{tree}</ConsentContext.Provider>;
}
